// Super Admin → "Conversazioni": tutte le chat tra clienti e consulenti, con
// ricerca per nome e periodo e apertura del thread completo. Sola lettura: lo
// staff non scrive nella conversazione, e ogni apertura finisce nello storico
// attività delle due parti.
import { useState } from 'react';
import { Spinner } from '@klaro7/ui';
import type { AdminChatConversationListItem } from '@klaro7/shared-types';

import { AdminConversationDrawer } from '@/features/chat/components/AdminConversationDrawer';
import { formatDateTime } from '@/features/chat/lib/adminChat';
import {
  useAdminConversationFacets,
  useAdminConversations,
} from '@/features/chat/hooks/useStarredMessages';
import { useDebouncedUrlSearch, useListUrlState } from '@/lib/listUrlState';

const PAGE_SIZE = 20;

const inputClass =
  'w-full rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-900 focus:border-brand-teal focus:outline-none focus:ring-2 focus:ring-brand-teal/20';

export function AdminConversationsPage() {
  const { get, set, page } = useListUrlState();
  const q = get('q');
  const professionalId = get('professionalId');
  const from = get('from');
  const to = get('to');
  const [searchInput, setSearchInput] = useDebouncedUrlSearch(q, (value) => set({ q: value }));
  const [openConversationId, setOpenConversationId] = useState<string | null>(null);

  const facets = useAdminConversationFacets();
  const { data, isLoading, isError, isFetching } = useAdminConversations({
    q: q || undefined,
    professionalId: professionalId || undefined,
    from: from || undefined,
    to: to || undefined,
    page,
    limit: PAGE_SIZE,
  });

  const hasFilters = !!(q || professionalId || from || to);
  const resetFilters = () => {
    setSearchInput('');
    set({ q: null, professionalId: null, from: null, to: null });
  };

  const items: AdminChatConversationListItem[] = data?.data ?? [];
  const pagination = data?.pagination;

  return (
    <div className="space-y-6">
      <div className="rounded-2xl bg-gradient-to-r from-brand-navy via-[#1c1a4d] to-brand-teal-600 p-6">
        <h1 className="text-2xl font-bold text-white">Conversazioni</h1>
        <p className="mt-1 text-sm text-white/70">
          Le chat tra clienti e consulenti, consultabili per la gestione delle contestazioni. Sola lettura.
        </p>
      </div>

      {/* Filtri */}
      <div className="rounded-xl border border-neutral-200 bg-white p-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <label className="block lg:col-span-2">
            <span className="mb-1 block text-xs font-medium text-neutral-500">Cerca</span>
            <input
              type="search"
              className={inputClass}
              placeholder="Nome, cognome o email di cliente o consulente"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-neutral-500">Consulente</span>
            <select
              className={inputClass}
              value={professionalId}
              onChange={(e) => set({ professionalId: e.target.value })}
            >
              <option value="">Tutti i consulenti</option>
              {facets.data?.professionals.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-neutral-500">Dal</span>
            <input
              type="date"
              className={inputClass}
              value={from}
              max={to || undefined}
              onChange={(e) => set({ from: e.target.value })}
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-neutral-500">Al</span>
            <input
              type="date"
              className={inputClass}
              value={to}
              min={from || undefined}
              onChange={(e) => set({ to: e.target.value })}
            />
          </label>
        </div>
        <div className="mt-3 flex items-center justify-between gap-3">
          <p className="text-xs text-neutral-400">
            La ricerca lavora su nomi ed email delle parti, mai sul testo dei messaggi.
          </p>
          <button
            type="button"
            onClick={resetFilters}
            disabled={!hasFilters}
            className="rounded-lg border border-neutral-200 px-3 py-2 text-sm font-medium text-neutral-700 transition hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Azzera filtri
          </button>
        </div>
      </div>

      {/* Lista */}
      <div className="rounded-xl border border-neutral-200 bg-white">
        <div className="flex items-center justify-between border-b border-neutral-100 px-5 py-3">
          <p className="text-sm text-neutral-500">
            {pagination ? `${pagination.total} conversazioni` : '—'}
          </p>
          {isFetching && !isLoading && <Spinner size="sm" />}
        </div>

        {isLoading ? (
          <div className="flex justify-center py-16">
            <Spinner size="lg" />
          </div>
        ) : isError ? (
          <p className="py-16 text-center text-sm text-neutral-500">
            Impossibile caricare le conversazioni.
          </p>
        ) : items.length === 0 ? (
          <div className="py-16 text-center">
            <p className="text-sm font-medium text-neutral-700">Nessuna conversazione</p>
            <p className="mt-1 text-xs text-neutral-400">
              {hasFilters ? 'Prova a modificare i filtri.' : 'Compariranno qui le chat tra clienti e consulenti.'}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-neutral-100">
            {items.map((it) => (
              <li key={it.id}>
                <button
                  type="button"
                  onClick={() => setOpenConversationId(it.id)}
                  className="grid w-full grid-cols-1 gap-2 px-5 py-4 text-left transition hover:bg-neutral-50 focus:bg-neutral-50 focus:outline-none md:grid-cols-[minmax(0,1.2fr)_minmax(0,1.6fr)_auto_auto] md:items-center md:gap-4"
                >
                  <p className="min-w-0 truncate text-sm font-medium text-neutral-900">
                    {it.user.name} ↔ {it.professional.name}
                  </p>
                  <p className="min-w-0 truncate text-sm text-neutral-500">
                    {it.lastMessagePreview ?? 'Nessun messaggio'}
                  </p>
                  <p className="whitespace-nowrap text-xs text-neutral-500">
                    {it.messageCount} {it.messageCount === 1 ? 'messaggio' : 'messaggi'}
                  </p>
                  <p className="whitespace-nowrap text-xs text-neutral-500">
                    {it.lastMessageAt ? formatDateTime(it.lastMessageAt) : '—'}
                  </p>
                </button>
              </li>
            ))}
          </ul>
        )}

        {pagination && pagination.totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-neutral-100 px-5 py-3 text-sm">
            <button
              type="button"
              disabled={!pagination.hasPrevPage}
              onClick={() => set({ page: Math.max(1, page - 1) })}
              className="rounded-lg px-3 py-1.5 font-medium text-neutral-700 hover:bg-neutral-100 disabled:opacity-40"
            >
              ← Precedenti
            </button>
            <span className="text-neutral-500">
              Pagina {pagination.page} di {pagination.totalPages}
            </span>
            <button
              type="button"
              disabled={!pagination.hasNextPage}
              onClick={() => set({ page: page + 1 })}
              className="rounded-lg px-3 py-1.5 font-medium text-neutral-700 hover:bg-neutral-100 disabled:opacity-40"
            >
              Successivi →
            </button>
          </div>
        )}
      </div>

      {openConversationId && (
        <AdminConversationDrawer
          conversationId={openConversationId}
          onClose={() => setOpenConversationId(null)}
        />
      )}
    </div>
  );
}
